import { call } from 'redux-saga/effects'
import { ApiService, callClientSecureApi } from './callApi'
import { Endpoints } from 'src/constants/endpoints'
import { MAX_FILE_SIZE_BYTES } from 'src/constants/fileUpload'

export interface GcsFileReferenceDto {
  gcsPath: string
  originalname: string
  mimeType: string
  size: number
}

export interface SignedUrlRequest {
  stepId: string
  originalname: string
  mimeType: string
  size: number
}

export interface SignedUrlResponse {
  signedUrl: string
  gcsPath: string
  expiresAt: string
}

export function* requestSignedUrl(req: SignedUrlRequest) {
  const { payload }: { payload: SignedUrlResponse } = yield call(
    callClientSecureApi,
    Endpoints.RequestUploadUrl,
    { method: 'POST', apiService: ApiService.Paraworks, body: req },
  )

  return payload as SignedUrlResponse
}

export async function putFileToGcs(
  signedUrl: string,
  file: File,
  signal?: AbortSignal,
): Promise<number> {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'x-goog-content-length-range': `0,${MAX_FILE_SIZE_BYTES}`,
    },
    body: file,
    signal,
  })

  return res.status
}

export function* uploadFileToGcs(
  req: SignedUrlRequest,
  file: File,
  overrideOriginalname?: string,
): Generator<unknown, GcsFileReferenceDto, any> {
  const controller = new AbortController()

  try {
    let { signedUrl, gcsPath } = (yield call(requestSignedUrl, req)) as SignedUrlResponse

    let status: number = yield call(putFileToGcs, signedUrl, file, controller.signal)

    if (status === 403) {
      const fresh = (yield call(requestSignedUrl, req)) as SignedUrlResponse
      signedUrl = fresh.signedUrl
      gcsPath = fresh.gcsPath
      status = yield call(putFileToGcs, signedUrl, file, controller.signal)
    }

    if (status < 200 || status >= 300) {
      throw new Error(`GCS upload failed: ${status}`)
    }

    return buildFileRef(file, gcsPath, overrideOriginalname)
  } finally {
    controller.abort()
  }
}

export function buildFileRef(
  file: File,
  gcsPath: string,
  overrideOriginalname?: string,
): GcsFileReferenceDto {
  return {
    gcsPath,
    originalname: overrideOriginalname ?? file.name,
    mimeType: file.type,
    size: file.size,
  }
}
